import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { SubscriptionTier, UserProfile } from '@/types/user'
import { FREE_TIER_SESSION_LIMIT } from '@/types/user'
import { useEffect, useState } from 'react'

interface SubscriptionState {
  profile: UserProfile | null
  loading: boolean
  error: string | null
}

interface UseSubscriptionReturn extends SubscriptionState {
  tier: SubscriptionTier
  isPro: boolean
  isAdmin: boolean
  isGiftedProActive: boolean
  sessionsRemaining: number
  isAtLimit: boolean
  refetch: () => Promise<void>
}

export function useSubscription(): UseSubscriptionReturn {
  const user = useAuthStore((s) => s.user)
  const [state, setState] = useState<SubscriptionState>({
    profile: null,
    loading: true,
    error: null,
  })

  async function fetchProfile() {
    if (!user) {
      setState({ profile: null, loading: false, error: null })
      return
    }

    setState((s) => ({ ...s, loading: true, error: null }))

    const { data, error } = await supabase.from('profiles').select('*').eq('id', user.id).single()

    if (error || !data) {
      setState({ profile: null, loading: false, error: 'Failed to load profile.' })
      return
    }

    const profile: UserProfile = {
      id: data.id,
      email: data.email ?? null,
      name: data.name,
      targetRole: data.target_role,
      experienceLevel: data.experience_level,
      targetIndustry: data.target_industry,
      subscriptionTier: data.subscription_tier,
      sessionsUsedThisMonth: data.sessions_used_this_month,
      isAdmin: data.is_admin ?? false,
      giftedProExpiresAt: data.gifted_pro_expires_at ?? null,
      sessionWindowStartsAt: data.session_window_starts_at,
    }

    setState({ profile, loading: false, error: null })
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchProfile is defined in scope and only depends on user, captured via user?.id
  useEffect(() => {
    void fetchProfile()
  }, [user?.id])

  const profile = state.profile
  const tier = profile?.subscriptionTier ?? 'free'
  const isGiftedProActive =
    !!profile?.giftedProExpiresAt && new Date(profile.giftedProExpiresAt) > new Date()
  const isPro = profile?.isAdmin === true || tier === 'pro' || isGiftedProActive
  const sessionsUsed = profile?.sessionsUsedThisMonth ?? 0
  const sessionsRemaining = isPro
    ? Number.POSITIVE_INFINITY
    : Math.max(0, FREE_TIER_SESSION_LIMIT - sessionsUsed)
  const isAtLimit = !isPro && sessionsUsed >= FREE_TIER_SESSION_LIMIT

  const isAdmin = profile?.isAdmin === true
  return {
    ...state,
    tier,
    isPro,
    isAdmin,
    isGiftedProActive,
    sessionsRemaining,
    isAtLimit,
    refetch: fetchProfile,
  }
}
